'use client';

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useClientStorage } from "@/lib/use-client-storage";

type MegaMenuId = "capabilities" | "services";

type MegaMenuSection = {
  title: string;
  description: string;
  items: Array<{
    title: string;
    description: string;
    href: string;
  }>;
};

type MegaMenuConfig = {
  label: string;
  eyebrow: string;
  title: string;
  description: string;
  featuredHref: string;
  featuredLabel: string;
  secondaryHref: string;
  secondaryLabel: string;
  sections: MegaMenuSection[];
};

const megaMenus: Record<MegaMenuId, MegaMenuConfig> = {
  capabilities: {
    label: "Capabilities",
    eyebrow: "Platform Depth",
    title: "Build on proven capability pages, then expand into adjacent platform work.",
    description:
      "GMX TEC combines detailed platform building blocks with architecture, delivery, and operations support for broader cloud and AI programs.",
    featuredHref: "/capabilities",
    featuredLabel: "Browse All Capabilities",
    secondaryHref: "/case-studies",
    secondaryLabel: "See Delivery Outcomes",
    sections: [
      {
        title: "Core Capability Pages",
        description: "Production platform topics you can explore in detail today.",
        items: [
          {
            title: "Compute Services",
            description: "Provisioned Linux compute for stable production workloads and predictable scaling.",
            href: "/capabilities/compute-services",
          },
          {
            title: "Kubernetes Platforms",
            description: "Secure multi-tenant Kubernetes design, delivery, and long-term operations.",
            href: "/capabilities/kubernetes-platforms",
          },
          {
            title: "Elastic & Block Storage",
            description: "Persistent storage tiers for databases, stateful apps, and durable services.",
            href: "/capabilities/elastic-and-block-storage",
          },
          {
            title: "Ceph Storage",
            description: "Software-defined object, block, and file storage at scale.",
            href: "/capabilities/ceph-storage",
          },
        ],
      },
      {
        title: "Cloud Platforms & Architecture",
        description: "Broader platform design areas that can route into discovery work now.",
        items: [
          {
            title: "Private Cloud Platforms",
            description: "OpenStack-led private cloud design for control, portability, and governance.",
            href: "/capabilities/private-cloud-platforms",
          },
          {
            title: "Networking & Connectivity",
            description: "VPC, SDN, VPN, segmentation, and secure interconnect patterns.",
            href: "/capabilities/networking-connectivity",
          },
          {
            title: "Infrastructure Foundations",
            description: "Linux, virtualization, hardware baselines, and reliability engineering.",
            href: "/capabilities/infrastructure-foundations",
          },
        ],
      },
      {
        title: "AI Infrastructure",
        description: "Applied AI platform support framed as delivery expertise, not empty product claims.",
        items: [
          {
            title: "GPU Compute",
            description: "GPU-backed environments for training, inference, and accelerated workloads.",
            href: "/capabilities/ai-platform-foundations",
          },
          {
            title: "AI Platform Foundations",
            description: "Model operations, data pipelines, serving, and platform governance.",
            href: "/capabilities/ai-platform-foundations",
          },
          {
            title: "RAG & Knowledge Systems",
            description: "Retrieval-enabled systems grounded in private data and operational control.",
            href: "/capabilities/rag-knowledge-systems",
          },
        ],
      },
      {
        title: "How We Deliver",
        description: "Choose the path from architecture advice to managed platform ownership.",
        items: [
          {
            title: "Managed Delivery",
            description: "Ongoing monitoring, upgrades, remediation, and operational ownership.",
            href: "/services",
          },
          {
            title: "Consultancy",
            description: "Architecture guidance, trade-off analysis, and technical decision support.",
            href: "/services",
          },
          {
            title: "Platform Discovery",
            description: "Scope the right platform before you commit to build, migrate, or modernize.",
            href: "/services/platform-discovery",
          },
        ],
      },
    ],
  },
  services: {
    label: "Services",
    eyebrow: "Delivery Models",
    title: "Move from strategy to managed execution with a clearer services path.",
    description:
      "GMX TEC supports advisory, migration, modernization, governance, and long-term operational delivery for platform teams that need practical ownership.",
    featuredHref: "/services",
    featuredLabel: "Explore Services",
    secondaryHref: "/contact",
    secondaryLabel: "Book a Consultation",
    sections: [
      {
        title: "Managed & Advisory Services",
        description: "Work with GMX TEC as operators, advisors, or both.",
        items: [
          {
            title: "Managed Delivery",
            description: "We help run the platform after launch, not just build it.",
            href: "/services/managed-services",
          },
          {
            title: "Consultancy",
            description: "Senior guidance on architecture, roadmap decisions, and execution trade-offs.",
            href: "/services/consultancy",
          },
          {
            title: "Technical Assessments",
            description: "Review platform risks, maturity, and modernization options.",
            href: "/services/technical-assessments",
          },
        ],
      },
      {
        title: "Transformation Services",
        description: "Support for migration, modernization, and automation-led change.",
        items: [
          {
            title: "Cloud Migration",
            description: "Move from legacy or incumbent platforms with lower operational friction.",
            href: "/services/cloud-migrations",
          },
          {
            title: "Platform Modernization",
            description: "Improve reliability, automation, and developer experience over time.",
            href: "/services/platform-modernization",
          },
          {
            title: "Automation & DevOps",
            description: "IaC, CI/CD, GitOps, and repeatable operational patterns.",
            href: "/services/automation-devops",
          },
        ],
      },
      {
        title: "Governance & Reliability",
        description: "Operational guardrails for security, resilience, and sustainable scale.",
        items: [
          {
            title: "Security & Compliance Support",
            description: "Hardening, governance, and controls for regulated environments.",
            href: "/services/compliance",
          },
          {
            title: "Resilience & Disaster Recovery",
            description: "Backup, recovery, and continuity planning for critical workloads.",
            href: "/services/resilience-disaster-recovery",
          },
          {
            title: "Performance & Cost Optimization",
            description: "Tune platforms for efficiency, stability, and sustainable growth.",
            href: "/services/performance-cost-optimization",
          },
        ],
      },
    ],
  },
};

export function SiteNav() {
  const navRef = useRef<HTMLElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [openMenu, setOpenMenu] = useState<MegaMenuId | null>(null);
  const [pinnedMenu, setPinnedMenu] = useState<MegaMenuId | null>(null);
  const isAuthenticated = Boolean(useClientStorage("refresh_token"));

  useEffect(() => {
    const handlePointerOutside = (event: MouseEvent) => {
      if (!navRef.current?.contains(event.target as Node)) {
        setOpenMenu(null);
        setPinnedMenu(null);
      }
    };

    const handleFocusOutside = (event: FocusEvent) => {
      if (!navRef.current?.contains(event.target as Node)) {
        setOpenMenu(null);
        setPinnedMenu(null);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenMenu(null);
        setPinnedMenu(null);
      }
    };

    document.addEventListener("mousedown", handlePointerOutside);
    document.addEventListener("focusin", handleFocusOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerOutside);
      document.removeEventListener("focusin", handleFocusOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const clearTimers = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openFromHover = (menu: MegaMenuId) => {
    if (pinnedMenu) {
      return;
    }

    clearTimers();
    setOpenMenu(menu);
  };

  const closeFromHover = () => {
    if (pinnedMenu) {
      return;
    }

    clearTimers();
    closeTimerRef.current = setTimeout(() => setOpenMenu(null), 180);
  };

  const toggleMenu = (menu: MegaMenuId) => {
    clearTimers();

    if (openMenu === menu && pinnedMenu === menu) {
      setOpenMenu(null);
      setPinnedMenu(null);
      return;
    }

    setOpenMenu(menu);
    setPinnedMenu(menu);
  };

  const openFromFocus = (menu: MegaMenuId) => {
    clearTimers();
    setOpenMenu(menu);
  };

  return (
    <nav ref={navRef} className="siteNav" aria-label="Primary">
      <ul className="navList">
        <li>
          <Link className="navLink" href="/">
            Home
          </Link>
        </li>

        {(Object.entries(megaMenus) as Array<[MegaMenuId, MegaMenuConfig]>).map(([menuId, menu]) => {
          const isOpen = openMenu === menuId;

          return (
            <li
              key={menuId}
              className={`megaNavItem ${isOpen ? "isOpen" : ""}`}
              onMouseEnter={() => openFromHover(menuId)}
              onMouseLeave={closeFromHover}
            >
              <button
                type="button"
                className={`navTrigger ${isOpen ? "isActive" : ""}`}
                aria-expanded={isOpen}
                aria-controls={`${menuId}-panel`}
                onMouseEnter={() => openFromHover(menuId)}
                onClick={() => toggleMenu(menuId)}
                onFocus={() => openFromFocus(menuId)}
                onKeyDown={(event) => {
                  if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setOpenMenu(menuId);
                    setPinnedMenu(menuId);
                  }
                }}
              >
                {menu.label}
                <span className={`navChevron ${isOpen ? "isOpen" : ""}`} aria-hidden="true">
                  ▾
                </span>
              </button>

              {isOpen ? (
                <div
                  id={`${menuId}-panel`}
                  className="megaPanel"
                  role="region"
                  aria-label={`${menu.label} menu`}
                  onMouseEnter={clearTimers}
                  onMouseLeave={closeFromHover}
                >
                  <div className="megaPanelInner">
                    <div className="megaColumns wide">
                      {menu.sections.map((section) => (
                        <section key={section.title} className="megaGroup" aria-label={section.title}>
                          <p className="megaSectionEyebrow">{section.title}</p>
                          <p className="megaCategoryDescription">{section.description}</p>
                          <ul className="megaList">
                            {section.items.map((item) => (
                              <li key={item.title} className="megaEntry">
                                <Link className="megaItemLink" href={item.href}>
                                  <p className="megaTitle">{item.title}</p>
                                  <p className="megaDescription">{item.description}</p>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </section>
                      ))}
                    </div>

                    <aside className="megaAside featured" aria-label={`${menu.label} featured summary`}>
                      <p className="eyebrow">{menu.eyebrow}</p>
                      <h3>{menu.title}</h3>
                      <p>{menu.description}</p>
                      <div className="megaAsideActions">
                        <Link className="cta mini" href={menu.featuredHref}>
                          {menu.featuredLabel}
                        </Link>
                        <Link className="cta mini secondary" href={menu.secondaryHref}>
                          {menu.secondaryLabel}
                        </Link>
                      </div>
                    </aside>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}

        <li>
          <Link className="navLink" href="/case-studies">
            Case Studies
          </Link>
        </li>
        <li>
          <Link className="navLink" href="/pricing">
            Pricing
          </Link>
        </li>
        <li>
          <Link className="navLink" href="/contact">
            Contact
          </Link>
        </li>
        <li>
          <Link className="navLink" href={isAuthenticated ? "/portal/overview" : "/portal"}>
            Portal
          </Link>
        </li>
      </ul>
    </nav>
  );
}
